<template>
  <div class="leaderboard-container">
    <el-table :data="leaderboardIndividual" style="width: 100%" max-height="800" :empty-text="$t('common.no_values')" @sort-change="sortChange" :default-sort="{ prop: sort_by, order: order_by }" v-loading="loadingIndividualLeaderboard">
      <el-table-column prop="id" :label="$t('charts.learner_col_label')">
        <template slot-scope="scope">
          <div class="member">
            <p class="index">{{ scope.$index + 1 }}</p>
            <a @click="goToMemberLink(scope.row)">
              <p>{{ name(scope.row) }}</p>
            </a>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="eps" sortable='custom'  :label="$t('charts.score_col_label')" :sort-orders="['descending', 'ascending']" width="256">
        <template slot="header">
          <el-tooltip :content="$t('charts.tooltips.individual_eps')">
            <span>{{ $t('charts.score_col_label') }}</span>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          <div class="elsa-score">
            <p>{{ Math.round(scope.row['eps']) }}%</p>
            <el-progress class="eps" :percentage="parseFloat(scope.row['eps'])" :show-text="false" :width="95"> </el-progress>
          </div>
        </template>
      </el-table-column>
      <el-table-column sortable='custom'  prop="durations" align="center" :label="$t('charts.duration_col_label')" :sort-orders="['descending', 'ascending']" width="256">
        <template slot="header">
          <el-tooltip :content="$t('charts.tooltips.individual_durations')">
            <span>{{ $t('charts.duration_col_label') }}</span>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          {{ intervalToHours(scope.row, scope.column, scope.row['durations'], scope.$index) }}
        </template>
      </el-table-column>
      <el-table-column prop="lessons_total" sortable='custom'  :label="$t('charts.lessons_total_col_label')" :sort-orders="['descending', 'ascending']" align="center" width="256">
        <template slot="header">
          <el-tooltip :content="$t('charts.tooltips.individual_lessons_total')">
            <span>{{ $t('charts.lessons_total_col_label') }}</span>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          {{ scope.row['lessons_total'] }}
        </template>
      </el-table-column>
    </el-table>
    <div v-if="leaderboardIndividual && leaderboardIndividual.length" class="table-footer more">
      <el-button type="text" @click.native="handleLoadMore" class="btn-more" icon="el-icon-caret-bottom">{{ $t('common.view_more') }}</el-button>
      <el-button type="text" @click.native="handleViewLess" class="btn-less" :disabled="currentpage <= 0" icon="el-icon-caret-top">{{ $t('common.view_less') }}</el-button>
      <el-form class="pagesize-form">
        <span class="pre-text">{{ $t('common.view') }}</span>
        <elsa-select 
          :options="pageSizeOption"
          name="page-size"
          @change="changePageSize"
          v-model.lazy="pageSize"
        />
        <span class="post-text">{{ $t('common.more') }}/{{ $t('common.less') }}</span>
      </el-form>
      <div class="over-table-pagination">Showing 1 - {{leaderboardIndividual.length}} of {{countIndividual}}</div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { warningMessage } from '../../../utils/messages'
import { always } from 'ramda'
import { is, toString, compose, invoker } from 'ramda'
import showNoReportDataWarning from '@/components/common/mixins/noReportData'
import { isSessionExpireError } from '@/utils/sessionExpireErr'
import ElsaSelect from '@/components/common/Select'
import { LEADERBOARD_PAGE_SIZE } from '@/constants/itemPerPage'
const padStart = compose(invoker(2, 'padStart')(2, '0'), toString)
export default {
  mixins: [showNoReportDataWarning],
  components: {
    ElsaSelect
  },
  computed: {
    numberElementsPerPage: always(5),
    ...mapGetters({
      getCurrentClass: 'classes/getCurrentClass'
    }),
    ...mapState('classes', ['leaderboardIndividual', 'loadingIndividualLeaderboard']),
    ...mapState('organization', ['currentOrganizationId']),
    sort_by_api() {
      if (this.sort_by === 'eps') {
        return 'score'
      } else if (this.sort_by === 'durations') {
        return 'duration'
      }
      return 'lesson'
    },
    order_by_api() {
      if (this.order_by === 'descending') {
        return 'desc'
      } else if (this.order_by === 'ascending') {
        return 'asc'
      }
      return 'desc'
    },
    startDateAndendDateAndsortBy() {
      return `${this.startDate}|${this.endDate}|${this.sort_by_api}|${this.order_by_api}`
    },
    currentpage() {
      return this.leaderboardIndividual.length > 0 ? Math.floor(this.leaderboardIndividual.length / this.pageSize) + (this.leaderboardIndividual.length % this.pageSize === 0 ? -1 : 0) : 0
    },
    pageSizeOption() {
      return LEADERBOARD_PAGE_SIZE;
    }
  },
  data() {
    return {
      sort_by: 'eps',
      order_by: 'descending',
      pageSize: 5,
    }
  },
  props: ['new', 'startDate', 'endDate', 'isActive', 'countIndividual'],
  methods: {
    name(userObject) {
      if (is(String, userObject)) {
        return userObject
      }

      var ret = userObject.email

      if (userObject.first_name || userObject.last_name) {
        ret = userObject ? `${userObject.first_name} ${userObject.last_name}` : ''
      }
      return ret
    },
    intervalToHours(row, column, cellValue, index) {
      if (!is(Number, cellValue)) {
        return '00:00:00'
      }
      const hours = Math.floor(cellValue / (60 * 60))
      const minutes = Math.floor((cellValue - hours * 3600) / 60)
      const seconds = cellValue - hours * 3600 - minutes * 60

      return `${padStart(hours)}:${padStart(minutes)}:${padStart(seconds)}`
    },
    async sortChange({ prop, order }) {
      this.sort_by = prop
      this.order_by = order
    },
    getIndividualLeaderboardData(options) {
      return this.getIndividualLeaderboard(options).catch((err) => {
        if(!isSessionExpireError(err))
          this.showNoReportDataWarning();
      });
    },
    ...mapActions({
      getIndividualLeaderboard: 'classes/getIndividualLeaderboard',
      lessIndividualLeaderboard: 'classes/lessIndividualLeaderboard',
    }),
    async handleLoadMore() {
      if (!this.getCurrentClass) {
        return
      }
      await this.getIndividualLeaderboardData({
        groupId: this.getCurrentClass.id,
        from: this.startDate,
        to: this.endDate,
        scope: this.scope,
        individual_cursor: this.currentpage + 1,
        individual_sort_by: this.sort_by_api,
        individual_order_by: this.order_by_api,
        page_size: this.pageSize,
        replace: false
      })
    },
    handleViewLess() {
      this.lessIndividualLeaderboard({page: this.currentpage - 1, page_size: this.pageSize})
    },
    async changePageSize(value) {
      this.pageSize = value;
      await this.getIndividualLeaderboardData({
        from: this.startDate,
        to: this.endDate,
        scope: this.scope,
        individual_cursor: 0,
        individual_sort_by: this.sort_by_api,
        individual_order_by: this.order_by_api,
        page_size: this.pageSize,
        replace: true
      })
    },
    goToMemberLink(row) {
      const org_id = this.currentOrganizationId;
      window.location =  `/organization/${org_id}/students/${row.id}`
    },
  },
  async mounted() {
    try {
      if (!this.getCurrentClass) {
        return
      }
      await this.getIndividualLeaderboardData({
        groupId: this.getCurrentClass.id,
        from: this.startDate,
        to: this.endDate,
        scope: this.scope,
        individual_cursor: 0,
        individual_sort_by: this.sort_by_api,
        individual_order_by: this.order_by_api,
        page_size: this.pageSize,
        replace: true
      })
    } catch (error) {
      return warningMessage(this.$t('common.generic_error_message'))
    }
  },
  destroyed() {
    this.$store.dispatch('classes/resetIndividualLeaderboard')
  },
  watch: {
    startDateAndendDateAndsortBy: {
      async handler(newValue, oldValue) {
        if (newValue !== oldValue && this.getCurrentClass) {
          await this.getIndividualLeaderboardData({
            groupId: this.getCurrentClass.id,
            from: this.startDate,
            to: this.endDate,
            scope: this.scope,
            individual_cursor: 0,
            individual_sort_by: this.sort_by_api,
            individual_order_by: this.order_by_api,
            page_size: this.pageSize,
            replace: true
          })
        }
      }
    },
    '$store.state.classes.currentClass.id'() {
      return this.getIndividualLeaderboardData({
        groupId: this.getCurrentClass.id,
        from: this.startDate,
        to: this.endDate,
        scope: this.scope,
        individual_cursor: 0,
        individual_sort_by: this.sort_by_api,
        individual_order_by: this.order_by_api,
        page_size: this.pageSize,
        replace: true
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/_colors.scss';

.team-tab-body {
  padding: 40px;
  text-align: center;
}

// button {
//   background: #007eff;
//   border-radius: 8px;
//   border: none;
//   height: 48px;
//   color: white;
//   padding: 16px;
//   font-size: 18px;
//   min-width: 215px;
//   cursor: pointer;
//   // font-weight: bold;
//   margin: 0 10px;
//   &:hover {
//     background: #0362c4;
//   }
// }
.no-item-title {
  font-weight: 600;
  font-size: 32px;
  text-align: center;
}
.no-item-desc {
  font-size: 18px;
}

.elsa-score {
  display: flex;
  align-items: center;

  .eps {
    width: 95px;
    margin-left: 12px;
  }
}

.leaderboard-container {
  display: flex;
  flex-direction: column;

  .table-footer.more {
    background-color: rgba(47, 128, 237, 0.1);
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    display: flex;
    justify-content: space-around;
    margin: 0px;

    .btn-more:not([disabled]),
    .btn-less:not([disabled]) {
      // background-color: rgba(47, 128, 237, 0.1);
      // margin: 0px;
      color: #2f80ed;
      // border-top-left-radius: 0px;
      // border-top-right-radius: 0px;
      font-size: 14px;

      &:hover,
      &.active,
      &.focus {
        color: #0362c4;
      }
    }
    .pagesize-form {
      width: 233px;
      padding: 10px 0px;
      display: flex;
      column-gap: 5px;
      .elsa-input {
        margin-bottom: 0px;
        width: 100%;
        ::v-deep .el-input__inner {
          height: 35px;
          opacity: 1;
        }
      }
      .pre-text {
        width: 100%;
        margin-top: 8px;
        text-align: right;
      }
      .post-text {
        width: 100%;
        margin-top: 8px;
      }
    }
    .over-table-pagination {
      padding: 20px 0px;
    }
  }

  &::v-deep .el-table__body {
    p,
    span {
      font-size: 16px;
      color: #121131;
      font-weight: 500;
    }
  }

  &::v-deep .el-table__header-wrapper .cell {
    font-size: 12px;
    line-height: 18px;
    font-weight: 500;
  }

  .member {
    display: flex;
    flex-direction: row;
    .index {
      margin-right: 15px;
    }
  }
}
</style>
